import React, { useState, useEffect, useRef } from 'react';
import { mergeClasses } from '../lib/mergeClasses';

interface ChatMessageProps {
  message: { text?: string; b64?: string; role?: string; }; // Define exact shape if possible
  AiName?: string;
  isAI?: boolean;
  isAudio?: boolean;
  animate?: boolean;
  onAnimationEnd?: () => void;
}

const TYPING_DELAY_MS = 25;

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, AiName, isAI = false, isAudio = false, animate = false, onAnimationEnd }) => {
  const [displayText, setDisplayText] = useState('');
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Use a ref to store the timer

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // ignore if not animating
    if (!animate) return;

    const text = message?.text || '';

    if (isAI && !isAudio && text) {
      let index = 0;
      setDisplayText(''); // Reset display text for new message

      timerRef.current = setInterval(() => {
        setDisplayText((prev) => {
          if (index < text.length) {
            const nextText = prev + text[index];
            index++;
            return nextText;
          } else {
            // Clear the timer when typing is complete
            clearInterval(timerRef.current!);
            timerRef.current = null;
            onAnimationEnd?.();
            return prev;
          }
        });
      }, TYPING_DELAY_MS);
    } else if (!isAudio && text) {
      setDisplayText(text);
    }

    // Cleanup: Clear the timer when the component unmounts or the message changes
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [message?.text, isAI, isAudio]);

  function getNamePrefix(name: string) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length > 2) {
      return parts.slice(0, 2).map(v => v[0]).join('')
    } else {
      return name.substring(0, 2)
    }
  }

  // Audio message
  if (isAudio) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[80%] rounded-lg p-4 bg-[#D32F2F] text-white">
          <audio controls className="w-full" />
        </div>
      </div>
    );
  }

  // Text message
  return (
    <div className={`flex ${isAI ? 'justify-start' : 'justify-end'} mb-4`}>
      {isAI && (
        <div className="w-8 h-8 !rounded-full !bg-[var(--sonaris-color-secondary)] flex items-center justify-center !mr-2">
          <span className="!text-white !text-sm !uppercase">{getNamePrefix(AiName || "Axx Intel")}</span>
        </div>
      )}
      <div className={mergeClasses('!max-w-[80%] !rounded-lg !p-4 !text-sm', {
        '!bg-[var(--sonaris-color-primary)]/10': isAI,
        '!bg-[var(--sonaris-color-secondary)]/20': !isAI,
      })}>
        {animate ? displayText : message.text}
      </div>
    </div>
  );
};
